import { useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import { Link, useSearchParams } from "react-router-dom"
import { listBankTransactions, listBudgetImpacts, listBudgets } from "../../ApiInterface"
import { useAccounts } from "../../AccountsContext"
import { accountPath, formatCurrency, regularBudgets, tricountBalances } from "../../Helpers"
import BudgetOperations from "./BudgetOperations"
import './Stats.scss'

// Stats : impact budget (montant × ratio d'impact) de toutes les opérations qui ont un budget, tous
// comptes confondus, rangées par mois de leur date. Frise par mois, trimestre ou année, barres
// empilées par budget ; un clic sur une période montre le détail par budget. L'objectif (montants des
// budgets) s'affiche quand tous les budgets sont sélectionnés, ou un seul. Les budgets à 0 € (Transfert)
// ne sont pas des dépenses : ni eux ni leurs opérations n'apparaissent. Avec un seul budget, la liste
// de ses opérations de la période remplace « Par budget ».
// La sélection est dans l'URL : /stats?granularity=quarter&budgets=8&period=2026-T3 (sans paramètre :
// mois, tous les budgets, période la plus récente).

type Granularity = 'month' | 'quarter' | 'year'

interface Period {
    key: string
    label: string
    sub: string
    title: string
    months: string[]
    current: boolean
}

const GRANULARITIES: [Granularity, string][] = [['month', 'Mois'], ['quarter', 'Trimestre'], ['year', 'Année']]
const isGranularity = (value: string | null): value is Granularity => GRANULARITIES.some(([granularity]) => granularity === value)
const PREVIOUS_PERIODS: Record<Granularity, string> = { month: 'des mois précédents', quarter: 'des trimestres précédents', year: 'des années précédentes' }
// Périodes visibles sans défiler : 12 mois, 2 ans en trimestres, 4 ans. Les plus anciennes sont à
// gauche, en faisant défiler la frise.
const VISIBLE_PERIODS: Record<Granularity, number> = { month: 12, quarter: 8, year: 4 }
const CHART_PADDING = 16
const PERIOD_GAP = 6
// Haut des barres dans la colonne : padding (6) + montant (14) + espace (6), comme dans Stats.scss
const PLOT_TOP = 26
const FULL_LENGTHS: Record<Granularity, number> = { month: 1, quarter: 3, year: 12 }
const CHART_HEIGHT = 170
const SHORT_MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

const formatRounded = (amount_cents: number) => formatCurrency(Math.round(amount_cents / 100) * 100).replace(/,00/, '')
const formatShort = (amount_cents: number) => {
    const euros = amount_cents / 100
    return Math.abs(euros) >= 1000
        ? `${(euros / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}k`
        : `${Math.round(euros)}`
}

// Objectif mensuel d'un budget, en centimes
const monthlyTarget = (budget: Budget) => budget.frequency === 'yearly' ? budget.amount_cents / 12 : budget.amount_cents

// Tous les mois du premier mois avec une opération jusqu'au mois en cours, « 2026-10 »
const monthRange = (first: string, last: string) => {
    const months = []
    for (let m = moment(first, 'YYYY-MM'); m.format('YYYY-MM') <= last; m.add(1, 'month')) {
        months.push(m.format('YYYY-MM'))
    }
    return months
}

const buildPeriods = (months: string[], granularity: Granularity, currentMonth: string): Period[] => {
    const monthIndex = (month: string) => Number(month.slice(5)) - 1
    if (granularity === 'month') {
        return months.map((month) => ({
            key: month,
            label: `${SHORT_MONTHS[monthIndex(month)]} ${month.slice(0, 4)}`,
            sub: '',
            title: `${MONTHS[monthIndex(month)]} ${month.slice(0, 4)}`,
            months: [month],
            current: month === currentMonth,
        }))
    }
    const groups = new Map<string, string[]>()
    months.forEach((month) => {
        const year = month.slice(0, 4)
        const key = granularity === 'quarter' ? `${year}-T${Math.floor(monthIndex(month) / 3) + 1}` : year
        groups.set(key, [...(groups.get(key) || []), month])
    })
    return Array.from(groups, ([key, groupMonths]) => {
        const [year, quarter] = key.split('-')
        const partial = groupMonths.length < (granularity === 'quarter' ? 3 : 12)
        const [firstMonth, lastMonth] = [groupMonths[0], groupMonths[groupMonths.length - 1]].map((month) => SHORT_MONTHS[monthIndex(month)])
        const range = firstMonth === lastMonth ? firstMonth : `${firstMonth} – ${lastMonth}`
        return {
            key,
            label: granularity === 'quarter' ? `${quarter} ${year}` : year,
            sub: granularity === 'year' && partial ? `${groupMonths.length} mois` : '',
            title: `${granularity === 'quarter' ? `${quarter} ${year}` : year}${partial ? ` (${range})` : ''}`,
            months: groupMonths,
            current: groupMonths.includes(currentMonth),
        }
    })
}

const Stats = () => {
    const currentMonth = moment().format('YYYY-MM')

    const [budgets, setBudgets] = useState([] as Budget[])
    const [impacts, setImpacts] = useState([] as BudgetImpact[])
    const { accounts } = useAccounts()
    const [searchParams, setSearchParams] = useSearchParams()
    const granularityParam = searchParams.get('granularity')
    const granularity: Granularity = isGranularity(granularityParam) ? granularityParam : 'month'
    // Budgets sélectionnés ; null = tous
    const selectedIds = useMemo(() => {
        const ids = (searchParams.get('budgets') || '').split(',').map(Number).filter((id) => id > 0)
        return ids.length ? ids : null
    }, [searchParams])
    const periodKey = searchParams.get('period')
    // Change la sélection dans l'URL, sans ajouter d'entrée à l'historique ; null retire le paramètre
    const updateParams = (changes: Record<string, string | null>) => setSearchParams((params) => {
        const next = new URLSearchParams(params)
        Object.entries(changes).forEach(([key, value]) => value === null ? next.delete(key) : next.set(key, value))
        return next
    }, { replace: true })
    const setSelectedIds = (ids: number[] | null) => updateParams({ budgets: ids ? ids.join(',') : null })
    const setPeriodKey = (key: string | null) => updateParams({ period: key })
    const [bankTransactions, setBankTransactions] = useState(null as BankTransaction[] | null)
    const chartRef = useRef<HTMLDivElement>(null)
    const [chartWidth, setChartWidth] = useState(0)

    useEffect(() => {
        listBudgets().then((budgets) => setBudgets(regularBudgets(budgets)
            .filter(({ amount_cents }) => amount_cents > 0)
            .sort((a, b) => monthlyTarget(b) - monthlyTarget(a))))
        listBudgetImpacts().then(setImpacts)
    }, [])

    // { "2026-10": { 3: 46200, ... } }
    const byMonth = useMemo(() => {
        const result: Record<string, Record<number, number>> = {}
        impacts.forEach(({ month, budget_id, amount_cents }) => {
            result[month] = { ...result[month], [budget_id]: amount_cents }
        })
        return result
    }, [impacts])

    const periods = useMemo(() => {
        const first = impacts.reduce((min, { month }) => month < min ? month : min, currentMonth)
        return buildPeriods(monthRange(first, currentMonth), granularity, currentMonth)
    }, [impacts, granularity, currentMonth])

    useEffect(() => {
        const chart = chartRef.current
        if(!chart) return
        const observer = new ResizeObserver(() => setChartWidth(chart.clientWidth))
        observer.observe(chart)
        return () => observer.disconnect()
    }, [])

    const visible = VISIBLE_PERIODS[granularity]
    const columnWidth = Math.max(24, (chartWidth - 2 * CHART_PADDING - PERIOD_GAP * (visible - 1)) / visible)
    const stackWidth = Math.min(120, Math.round(columnWidth * .6))

    // Les périodes les plus récentes à droite
    useEffect(() => {
        const chart = chartRef.current
        if(chart) chart.scrollLeft = chart.scrollWidth
    }, [periods.length, granularity, chartWidth])

    const selectedBudgets = selectedIds === null ? budgets : budgets.filter(({ id }) => selectedIds.includes(id))
    const allSelected = selectedIds === null
    const showTarget = allSelected || selectedBudgets.length === 1
    const period = periods.find(({ key }) => key === periodKey) || periods[periods.length - 1]

    const amount = (p: Period, budgetId: number) => p.months.reduce((sum, month) => sum + (byMonth[month]?.[budgetId] || 0), 0)
    const total = (p: Period) => selectedBudgets.reduce((sum, budget) => sum + amount(p, budget.id), 0)
    // Objectif d'un mois, d'un trimestre ou d'une année entière, le même pour toutes les périodes
    const fullLength = FULL_LENGTHS[granularity]
    const budgetTarget = (budget: Budget) => monthlyTarget(budget) * fullLength
    const periodTarget = selectedBudgets.reduce((sum, budget) => sum + budgetTarget(budget), 0)

    const maxHeight = Math.max(1, ...periods.flatMap((p) => [
        selectedBudgets.reduce((sum, budget) => sum + Math.max(0, amount(p, budget.id)), 0),
        showTarget ? periodTarget : 0,
    ]))
    const barHeight = (amount_cents: number) => CHART_HEIGHT * Math.max(0, amount_cents) / maxHeight

    // Quand tous les budgets sont sélectionnés, un clic isole le budget ; ensuite il l'ajoute ou le retire.
    const toggleBudget = (id: number) => {
        if(selectedIds === null) return setSelectedIds([id])
        const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
        setSelectedIds(next.length && next.length < budgets.length ? next : null)
    }
    const isolateBudget = (id: number) => setSelectedIds(selectedIds?.length === 1 ? null : [id])
    const changeGranularity = (value: Granularity) => updateParams({ granularity: value === 'month' ? null : value, period: null })

    // Un seul budget : ses opérations de la période
    const singleBudget = selectedBudgets.length === 1 ? selectedBudgets[0] : null
    const [from, to] = period ? [period.months[0], period.months[period.months.length - 1]] : [null, null]
    useEffect(() => {
        setBankTransactions(null)
        if(!singleBudget || !from || !to) return
        let cancelled = false
        listBankTransactions({ budget_id: singleBudget.id, from, to })
            .then((bankTransactions) => { if(!cancelled) setBankTransactions(bankTransactions) })
            .catch((e) => console.warn('Opérations non chargées', e))
        return () => { cancelled = true }
    }, [singleBudget?.id, from, to]) // eslint-disable-line react-hooks/exhaustive-deps

    if(!period) return null

    const periodTotal = total(period)
    // Solde des Tricount à ce jour, indépendant de la période et des budgets
    const tricounts = tricountBalances(accounts)
    const tricountsOpen = searchParams.get('tricounts') === '1'
    // Moyenne des 12 périodes complètes précédentes, ramenée au nombre de mois de la période (trimestre
    // ou année en cours, première période de l'historique)
    const previous = periods.filter((p) => p.key < period.key && p.months.length === fullLength).slice(-12)
    const average = previous.length
        ? previous.reduce((sum, p) => sum + total(p), 0) / previous.length * period.months.length / fullLength
        : null
    const rows = selectedBudgets
        .map((budget) => ({ budget, value: amount(period, budget.id), target: budgetTarget(budget) }))
        .sort((a, b) => b.value - a.value)
    const maxRow = Math.max(1, ...rows.map(({ value }) => value))

    return <div className="stats-page">
        <div className="tl-page-head">
            <div>
                <h2>Stats</h2>
                <div className="sub">Impact budget de tous les comptes</div>
            </div>
        </div>

        <div className="stats-toolbar">
            <div className="segmented">
                { GRANULARITIES.map(([value, label]) =>
                    <button key={value} className={granularity === value ? 'on' : ''} onClick={() => changeGranularity(value)}>{ label }</button>
                )}
            </div>
            <div className="chips">
                <button className={`chip all${allSelected ? ' on' : ''}`} onClick={() => setSelectedIds(null)}>Tous les budgets</button>
                { budgets.map((budget) =>
                    <button key={budget.id} className={`chip${selectedIds !== null && selectedIds.includes(budget.id) ? ' on' : ''}`} onClick={() => toggleBudget(budget.id)}>
                        <i style={{ background: budget.color }}/>{ budget.name }
                    </button>
                )}
            </div>
        </div>

        { showTarget && <div className="stats-legend">
            <span><i className="target"/>Objectif</span>
            <span><i className="current"/>En cours</span>
        </div> }

        <div className="tl-card stats-chart" ref={chartRef}>
          <div className="periods">
            { showTarget && <span className="target" style={{ top: PLOT_TOP + CHART_HEIGHT - barHeight(periodTarget) }}/> }
            { periods.map((p) => {
                const value = total(p)
                return <button
                    key={p.key}
                    style={{ flexBasis: columnWidth }}
                    className={`period${p.key === period.key ? ' selected' : ''}${p.current ? ' current' : ''}`}
                    title={`${p.title} · ${formatRounded(value)}${showTarget ? ` / objectif ${formatRounded(periodTarget)}` : ''}`}
                    onClick={() => setPeriodKey(p.key)}>
                    <span className="num">{ formatShort(value) }</span>
                    <span className="plot" style={{ height: CHART_HEIGHT }}>
                        <span className="stack" style={{ width: stackWidth }}>
                            { selectedBudgets.map((budget) => {
                                const height = barHeight(amount(p, budget.id))
                                return height > 0 && <span key={budget.id} style={{ height, background: budget.color }}/>
                            })}
                        </span>
                    </span>
                    <span className="label">{ p.label }</span>
                    <span className="sub">{ p.current ? <span className="now">en cours</span> : p.sub }</span>
                </button>
            })}
          </div>
        </div>

        <div className="stats-detail">
            <div className="side">
                <div className="tl-card tl-kpi dark">
                    <div className="l">{ period.title }{ period.current ? ' · en cours' : '' }</div>
                    <div className="n">{ formatRounded(periodTotal) }</div>
                    <div className="hint">
                        { allSelected ? 'tous les budgets' : `${selectedBudgets.length} budget${selectedBudgets.length > 1 ? 's' : ''} sur ${budgets.length}` }
                        <br/>
                        { average === null || average === 0
                            ? "pas d'historique pour comparer"
                            : <><b className={periodTotal > average ? 'up' : 'down'}>
                                { periodTotal > average ? '+' : '−' }{ Math.abs(Math.round((periodTotal - average) / average * 100)) } %
                              </b> vs moyenne { PREVIOUS_PERIODS[granularity] } ({ formatRounded(average) })</> }
                    </div>
                </div>
                { showTarget && <div className="tl-card tl-kpi">
                    <div className="l">{ periodTotal > periodTarget ? 'Dépassement' : 'Reste' } · objectif { formatRounded(periodTarget) }</div>
                    <div className={`n${periodTotal > periodTarget ? ' over' : ''}`}>{ formatRounded(Math.abs(periodTarget - periodTotal)) }</div>
                    { selectedBudgets.length > 1 && <div className="hint">
                        { rows.filter(({ value, target }) => value > target).length } budget(s) au-dessus de leur objectif
                    </div> }
                </div> }
                { tricounts.rows.length > 0 && <button className={`tl-card tl-kpi tricount-kpi${tricountsOpen ? ' open' : ''}`}
                    aria-expanded={tricountsOpen}
                    onClick={() => updateParams({ tricounts: tricountsOpen ? null : '1' })}>
                    <div className="l">Tricount · { tricounts.total_cents < 0 ? 'vous devez' : 'on vous doit' }</div>
                    <div className={`n ${tricounts.total_cents < 0 ? 'owing' : 'owed'}`}>{ formatRounded(Math.abs(tricounts.total_cents)) }</div>
                    <div className="hint">solde net à ce jour · { tricountsOpen ? 'masquer' : 'voir le détail' }</div>
                </button> }
                { tricountsOpen && tricounts.rows.length > 0 && <div className="tl-card tricount-rows">
                    { tricounts.rows.map((account) => {
                        const balance = account.tricount_balance_cents || 0
                        return <Link key={account.id} to={accountPath(account)} className={`tricount-row${balance === 0 ? ' settled' : ''}`}>
                            <span className="name">{ account.name }
                                { account.tricount_last_synced_at && <small>synchro { moment(account.tricount_last_synced_at).fromNow() }</small> }
                            </span>
                            <span className={`amount ${balance < 0 ? 'owing' : balance > 0 ? 'owed' : ''}`}>{ formatCurrency(balance) }</span>
                        </Link>
                    })}
                </div> }
            </div>

            { singleBudget ? <BudgetOperations
                budget={singleBudget}
                months={period.months}
                title={period.title}
                bankTransactions={bankTransactions}
                accounts={accounts}/> :
            <div className="tl-card budget-rows">
                <h3><span>Par budget</span><span>{ selectedIds?.length === 1 ? 'clic = tous les budgets' : 'clic = isoler le budget' }</span></h3>
                { rows.map(({ budget, value, target }) => {
                    const over = showTarget && value > target
                    return <button key={budget.id} className={`budget-row${showTarget ? ' with-target' : ''}${over ? ' over' : ''}${value ? '' : ' zero'}`} onClick={() => isolateBudget(budget.id)}>
                        <span className="dot" style={{ background: budget.color }}/>
                        <span className="name">{ budget.name }</span>
                        <span className="track">
                            <span className="fill" style={{
                                width: `${Math.max(0, Math.min(100, showTarget ? value / Math.max(1, target) * 100 : value / maxRow * 100))}%`,
                                background: over ? undefined : budget.color,
                            }}/>
                        </span>
                        { showTarget
                            ? <span className="amount">
                                { formatRounded(value) } <small>/ { formatRounded(target) }</small>
                                <span className="state">{ over ? `dépassé de ${formatRounded(value - target)}` : `reste ${formatRounded(target - value)}` }</span>
                              </span>
                            : <>
                                <span className="amount">{ formatRounded(value) }</span>
                                <span className="share">{ periodTotal ? Math.round(value / periodTotal * 100) : 0 } %</span>
                              </> }
                    </button>
                })}
                <div className="hint-line"><i className="fa-solid fa-list"/>Isole un budget pour voir ses opérations</div>
            </div> }
        </div>
    </div>
}

export default Stats
